/**
 * Tariff Classification Service
 *
 * API service for HS code classification using Mistral AI
 */

import api from '../shared/services/api';

// ============================================================================
// Types
// ============================================================================

export interface ClassificationRequest {
  product_description: string;
  materials?: string;
  origin_country?: string;
  intended_use?: string;
  additional_context?: Record<string, any>;
  return_alternatives?: boolean;
}

export interface BatchClassificationRequest {
  products: Array<{
    description: string;
    materials?: string;
    origin?: string;
    use?: string;
    context?: Record<string, any>;
  }>;
  return_alternatives?: boolean;
}

export interface AlternativeHSCode {
  hs_code: string;
  confidence: number;
  description: string;
}

export interface ConfidenceFactors {
  description_clarity?: number;
  context_availability?: number;
  chapter_certainty?: number;
}

export interface ClassificationResponse {
  hs_code: string | null;
  confidence: number;
  description?: string;
  chapter?: string;
  chapter_name?: string;
  heading?: string;
  heading_name?: string;
  reasoning?: string;
  alternatives?: AlternativeHSCode[];
  confidence_factors?: ConfidenceFactors;
  validation_warning?: string;
  error?: string;
}

// ============================================================================
// EU Shipping Compliance
// ============================================================================

export type ShippingVerdictLevel = 'PROHIBITED' | 'CONDITIONS_APPLY' | 'CLEAR';

export interface FootnoteDetail {
  code: string;
  description: string;
}

export interface MeasureDetail {
  measure_type_id: string;
  measure_type_description: string;
  series: string;
  geographical_area: string;
  conditions_count: number;
  legal_act_codes: string[];
  footnotes: FootnoteDetail[];
}

export interface DocumentDetail {
  code: string;
  description: string;
  requirement: string;
}

export interface LegalActDetail {
  regulation_code: string;
  url: string;
}

export type CaveatCode =
  | 'ARMS_AND_AMMUNITION'
  | 'EXPLOSIVES_PYROTECHNICS'
  | 'RADIOACTIVE_NUCLEAR'
  | 'RADIOACTIVE_ISOTOPES'
  | 'COMMODITY_FOOTNOTE';

export interface Caveat {
  code: CaveatCode | string;  // open enum — backend may emit codes the FE doesn't know yet
  detail: string;
}

export interface ShippingCheckRequest {
  hs_code: string;
  destination_country: string;
  origin_country?: string;
}

export interface ShippingCheckResponse {
  verdict: ShippingVerdictLevel;
  commodity_code: string;
  commodity_description: string;
  country_code: string;
  import_restrictions: MeasureDetail[];
  export_restrictions: MeasureDetail[];
  required_documents: DocumentDetail[];
  legal_references: LegalActDetail[];
  duty_rate: string;
  caveats: Caveat[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Classify multiple products in batch
 */
export const classifyBatch = async (
  request: BatchClassificationRequest
): Promise<ClassificationResponse[]> => {
  console.log(
    `[Tariff Classifier] Batch REQUEST — ${request.products.length} product(s):`,
    request.products.map((p, i) => ({ index: i, description: p.description })),
  );

  const response = await api.post('/api/v1/tariff-classifier/classify-batch', request, {
    timeout: 120000, // 2 minutes for batch classification
  });

  console.log(
    '[Tariff Classifier] Batch RESPONSE:',
    response.data.map((r: any, i: number) => ({
      index: i,
      hs_code: r.hs_code,
      confidence: r.confidence,
      description: r.description,
      error: r.error,
    })),
  );

  return response.data;
};

/**
 * EU shipping-compliance check: HS code + destination → verdict
 */
export const checkShipping = async (
  request: ShippingCheckRequest
): Promise<ShippingCheckResponse> => {
  const response = await api.post('/api/v1/tariff-classifier/shipping-check', request, {
    timeout: 30000,
  });
  return response.data;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format confidence as percentage
 */
export const formatConfidence = (confidence: number): string => {
  return `${Math.round(confidence * 100)}%`;
};

/**
 * Get confidence color
 */
export const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.9) return 'success';
  if (confidence >= 0.75) return 'info';
  if (confidence >= 0.6) return 'warning';
  return 'error';
};

/**
 * Format HS code for display (add spaces for readability)
 */
export const formatHSCode = (hsCode: string): string => {
  if (!hsCode) return '';

  // Remove existing spaces/hyphens
  const clean = hsCode.replace(/[\s-]/g, '');

  // Format as: XX.XX.XX.XX (2-2-2-2)
  if (clean.length === 8) {
    return `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4, 6)}.${clean.slice(6, 8)}`;
  }

  // Format as: XX.XX.XX (2-2-2)
  if (clean.length === 6) {
    return `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4, 6)}`;
  }

  // Format as: XX.XX (2-2)
  if (clean.length === 4) {
    return `${clean.slice(0, 2)}.${clean.slice(2, 4)}`;
  }

  return clean;
};

/**
 * Get suggested improvements for classification
 */
export const getSuggestionText = (
  confidence: number,
  alternatives?: AlternativeHSCode[]
): string => {
  if (confidence >= 0.9) {
    return 'High confidence classification. You can proceed with this HS code.';
  }

  if (confidence >= 0.75) {
    return 'Good confidence. Consider reviewing the alternatives if available.';
  }

  if (confidence >= 0.6) {
    return 'Moderate confidence. Please review carefully and consider alternatives.';
  }

  return 'Low confidence. Manual review required. Check alternatives or consult a customs expert.';
};

/**
 * Extract product materials from description
 */
export const extractMaterialsFromDescription = (description: string): string | undefined => {
  const materialKeywords = [
    'plastic',
    'steel',
    'aluminum',
    'wood',
    'cotton',
    'polyester',
    'leather',
    'glass',
    'silicon',
    'copper',
    'brass',
    'titanium',
    'ceramic',
    'rubber',
  ];

  const lowerDesc = description.toLowerCase();
  const foundMaterials = materialKeywords.filter((material) =>
    lowerDesc.includes(material)
  );

  return foundMaterials.length > 0 ? foundMaterials.join(', ') : undefined;
};

/**
 * Build classification context from ATLAS data
 */
export const buildContextFromATLAS = (positionData: any): Partial<ClassificationRequest> => {
  return {
    product_description: positionData?.wareWarenbezeichnung || '',
    materials: positionData?.materials || extractMaterialsFromDescription(positionData?.wareWarenbezeichnung || ''),
    origin_country: positionData?.ursprungsland || '',
    additional_context: {
      weight: positionData?.wareEigenmasse,
      quantity: positionData?.menge,
      unit: positionData?.masseinheit,
    },
  };
};

/**
 * Auto-classify all positions in ATLAS data
 */
export const autoClassifyAllPositions = async (
  positions: any[]
): Promise<Array<{ positionIndex: number; classification: ClassificationResponse }>> => {
  const products = positions.map((pos, idx) => ({
    description: pos.wareWarenbezeichnung || '',
    materials: pos.materials || extractMaterialsFromDescription(pos.wareWarenbezeichnung || ''),
    origin: pos.ursprungsland || '',
    use: pos.intended_use || '',
    context: {
      weight: pos.wareEigenmasse,
      quantity: pos.menge,
      unit: pos.masseinheit,
      positionIndex: idx,
    },
  }));

  const results = await classifyBatch({ products, return_alternatives: false });

  return results.map((classification, idx) => ({
    positionIndex: idx,
    classification,
  }));
};

// ============================================================================
// Export Default
// ============================================================================

export default {
  classifyBatch,
  checkShipping,
  formatConfidence,
  getConfidenceColor,
  formatHSCode,
  getSuggestionText,
  extractMaterialsFromDescription,
  buildContextFromATLAS,
  autoClassifyAllPositions,
};
